<template>
  <div class="container-fluid px-0 aip-form">
    <div class="row" v-if="loadingCatalogos">
      <div class="col-12">
        <span class="hint">Cargando catálogo de Módulos/Secciones…</span>
      </div>
    </div>

    <div class="aip-card">
      <div class="aip-section-header aip-icon-title-row d-flex align-items-center mb-2">
        <span class="aip-section-icon"><i class="fas fa-id-badge"></i></span>
        <SectionTitle has-line content="Identificación" />
      </div>

      <!--
        26 ago 2026: Astianax pidió que la pantalla explique qué es cada sección (a partir del
        HTML propuesto, que sí traía estas definiciones bajo cada encabezado) -- este párrafo
        resume la clave real de la instrucción, tomado del índice único filtrado real
        UX_CODE_AIPromptConfiguration_UnaPublicadaPorClave (AIPromptModuloId, SeccionArea) WHERE
        Publicado -- no un dato inventado, ya estaba documentado solo en el schema de BD.
      -->
      <p class="hint mb-2">
        Módulo y Sección/Área definen la clave real de esta instrucción -- a qué parte del PEI
        aplica, y solo puede haber UNA versión Publicada a la vez para esa misma combinación. El
        Nombre descriptivo es solo para identificarla en el historial y no forma parte de esa
        clave.
      </p>

      <div class="row">
        <div class="col-12 col-md-3">
          <ValidationProvider v-slot="{ errors }" rules="required">
            <custom-drop-down
              v-model="module"
              :options="modulos"
              label="Módulo *"
              fieldkey="value"
              fieldname="text"
              placeholder="Seleccione"
              :disabled="isEditMode"
              :error="errors[0]"
            />
          </ValidationProvider>
        </div>

        <div class="col-12 col-md-3">
          <ValidationProvider v-slot="{ errors }" rules="required">
            <custom-drop-down
              v-model="sectionArea"
              :options="secciones"
              label="Sección / Área *"
              fieldkey="value"
              fieldname="text"
              placeholder="Seleccione"
              :disabled="isEditMode"
              :error="errors[0]"
            />
          </ValidationProvider>
        </div>

        <div class="col-12 col-md-3">
          <!--
            Nombre descriptivo: columna nueva CODE_AIPromptConfiguration.NombreDescriptivo
            (nullable, agregada 24 ago 2026 a pedido explícito). Puramente presentacional --
            solo ayuda a identificar esta instrucción en las pantallas de búsqueda/historial, no
            participa en la clave (Módulo, SeccionArea) ni en ninguna lógica de negocio.
          -->
          <custom-input
            v-model="value.displayName"
            label="Nombre descriptivo"
            placeholder="Ej. Comentario general - Nivel académico"
            maxlength="200"
            :disabled="readOnly"
          />
        </div>
      </div>
    </div>

    <div class="aip-card mt-3">
      <div class="aip-section-header aip-icon-title-row d-flex align-items-center mb-2">
        <span class="aip-section-icon"><i class="fas fa-book"></i></span>
        <SectionTitle has-line content="Contexto de aplicación" />
      </div>

      <!-- Campo documental -- mismo hecho ya explicado en el comentario junto a
           value.referenceCurriculum más abajo, ahora también visible en pantalla. -->
      <p class="hint mb-2">
        Campo documental -- el motor real hoy NO lo usa para decidir qué instrucción resolver,
        solo deja constancia de para qué currículo se redactó esta versión.
      </p>

      <div class="row">
        <div class="col-12 col-md-4">
          <!--
            Currículo de referencia: valor único (no selección múltiple) -- refleja lo que
            CurriculoReferencia realmente guarda en BD hoy (string simple, sin catálogo propio).
            El motor NO usa este valor para decidir qué instrucción resolver (ver conversación
            "Contexto de aplicación", 24 ago 2026, en pausa) -- es solo descriptivo/documental.
            Se mantiene como valor único (no checklist) a pedido explícito del usuario.
          -->
          <custom-drop-down
            v-model="value.referenceCurriculum"
            :options="curriculos"
            label="Currículo de referencia"
            fieldkey="value"
            fieldname="text"
            placeholder="No especifica"
            clearable
            :disabled="readOnly"
          />
        </div>
      </div>
    </div>

    <div class="aip-card mt-3">
      <div class="aip-section-header aip-icon-title-row d-flex align-items-center mb-2">
        <span class="aip-section-icon"><i class="fas fa-comment-alt"></i></span>
        <SectionTitle has-line content="Contenido de la instrucción" />
      </div>

      <!--
        26 ago 2026: a pedido de Astianax ("agrega la definición de las secciones, ejemplo qué
        se supone es el System Message, el Prompt..."), tomado del mismo texto que ya estaba
        propuesto en el HTML entregado (bloque role-label/role-hint por cada mensaje). Se agrega
        aquí, no solo como comentario de código, porque es justo lo que alguien redactando una
        instrucción necesita ver en pantalla antes de escribir.
      -->
      <p class="hint mb-3">
        Dos mensajes independientes que se envían al modelo -- columnas reales
        <code>SystemMessageText</code> y <code>PromptText</code> de
        <code>CODE_AIPromptConfiguration</code> -- ambos se guardan y se publican tal como se
        escriben aquí.
      </p>

      <div class="row">
        <div class="col-12">
          <p class="aip-role-label mb-1">
            <i class="fas fa-thumbtack mr-1"></i>System Message
            <span class="aip-role-tag">-- rol "system"</span>
          </p>
          <p class="aip-role-hint">
            Define quién es el asistente, qué normativa lo rige y las reglas que nunca debe
            romper (qué no inventar, cómo tratar una fuente sin dato, formato exacto de salida).
            No cambia entre estudiantes -- es la parte más estable de la instrucción.
          </p>
          <ValidationProvider v-slot="{ errors }" rules="required">
            <!--
              :maxlength es obligatorio en CustomTextArea (prop required: true) -- sin él, el
              componente usa su default numérico (0) y updateValue() trunca CUALQUIER tecleo a ''
              de inmediato (remainingChars = 0 - length siempre da negativo). No es un límite de
              negocio real, solo un techo generoso para que el campo sea utilizable.
              :rows se subió de 6 a 14 y el textarea interno se estira por CSS (.aip-textarea-full,
              ver <style>) para que el contenido real (a veces varios párrafos) se vea completo
              sin recortarse, como se pidió.
            -->
            <custom-text-area
              v-model="value.systemMessageText"
              label="System Message *"
              class="aip-textarea-full"
              :rows="14"
              :maxlength="8000"
              placeholder="Eres un asistente pedagógico especializado en..."
              :error="errors[0]"
              :disabled="readOnly"
            />
          </ValidationProvider>
        </div>

        <div class="col-12 mt-3">
          <p class="aip-role-label mb-1">
            <i class="fas fa-pen mr-1"></i>Prompt
            <span class="aip-role-tag">-- rol "user"</span>
          </p>
          <p class="aip-role-hint">
            La tarea puntual para este caso: qué debe redactar el modelo y con qué información
            debe integrarlo.
          </p>
          <ValidationProvider v-slot="{ errors }" rules="required">
            <custom-text-area
              v-model="value.promptText"
              label="Prompt Text *"
              class="aip-textarea-full"
              :rows="14"
              :maxlength="8000"
              :placeholder="promptTextPlaceholder"
              :error="errors[0]"
              :disabled="readOnly"
            />
          </ValidationProvider>
          <p class="hint">
            El único marcador real que se resuelve en tiempo de ejecución es
            <code>{{ contextoToken }}</code> -- ahí se inyecta el bloque armado a partir de las
            fuentes marcadas abajo. No se resuelven otros marcadores dentro de este texto.
          </p>
        </div>
      </div>
    </div>

    <div class="aip-card mt-3">
      <div class="d-flex align-items-center justify-content-between mb-2">
        <div class="aip-section-header aip-icon-title-row d-flex align-items-center">
          <span class="aip-section-icon"><i class="fas fa-database"></i></span>
          <SectionTitle has-line content="Fuentes de contexto" />
        </div>
        <span v-if="loadingFuentes" class="hint">Cargando catálogo…</span>
      </div>

      <p class="hint mb-2">
        Marca las fuentes que el Servicio de Agregación de Contexto debe resolver para esta
        instrucción. Catálogo real (CODE_AIPromptSource) -- las que aparecen deshabilitadas
        todavía no tienen un resolver implementado en el motor.
      </p>

      <label class="aip-fuentes-label">Fuentes de contexto a incluir</label>

      <!--
        Selector colapsable con resumen ("N seleccionadas") armado a mano -- MiPE no tiene un
        componente propio para esto: se revisó "vue-select" (está en package.json pero no se usa
        en ningún archivo real del proyecto) y "CheckBox"/"custom-checkbox-single-option" son
        casillas individuales, no un selector múltiple con resumen colapsado. Mismo patrón que
        "Parámetros avanzados" más abajo en este archivo (toggle booleano + v-show, en vez de
        depender de un componente de terceros no verificado para este caso puntual).
      -->
      <div
        class="aip-multiselect"
        :class="{ 'aip-multiselect-open': fuentesExpanded, 'aip-multiselect-disabled': readOnly }"
      >
        <button
          type="button"
          class="aip-multiselect-trigger"
          :disabled="readOnly"
          @click="fuentesExpanded = !fuentesExpanded"
        >
          <span>{{ fuentesSeleccionadasLabel }}</span>
          <i class="fas" :class="fuentesExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </button>

        <div v-if="fuentesExpanded" class="aip-multiselect-panel">
          <custom-checkbox-single-option
            v-for="fuente in fuentesCatalogo"
            :key="fuente.aiPromptSourceId"
            :value="isFuenteSeleccionada(fuente.name)"
            :option-text="fuente.name + (fuente.implementadoFlag === false ? ' (no implementado aún)' : '')"
            :disabled="readOnly || fuente.implementadoFlag === false"
            class="aip-fuente-checkbox"
            @input="(checked) => onFuenteToggle(fuente.name, checked)"
          />
        </div>
      </div>

      <!--
        Tarjetas de detalle: una por cada fuente YA marcada, con su Description real
        (CODE_AIPromptSource.Description) -- el nombre se muestra con espacios insertados entre
        palabras (formatFuenteName) para que se lea mejor, pero es el mismo valor real, solo
        reformateado (no una traducción ni una etiqueta inventada). El mockup entregado tenía
        además una segunda línea gris tipo "MiPE Académico · ..." -- esa no se agrega aquí porque
        no existe como dato real en CODE_AIPromptSource (solo Name + Description); agregarla
        habría significado inventar contenido, y el criterio de este módulo es ser transparente
        con los huecos reales en vez de fabricar datos que no existen en el sistema.
      -->
      <div v-if="fuentesSeleccionadas.length" class="aip-fuente-cards mt-2">
        <div
          v-for="fuente in fuentesSeleccionadas"
          :key="'card-' + fuente.aiPromptSourceId"
          class="aip-fuente-card"
        >
          <div class="aip-fuente-card-title">
            {{ formatFuenteName(fuente.name) }}
            <i
              v-if="fuente.description"
              class="fas fa-question-circle aip-fuente-card-icon"
              :title="fuente.description"
            ></i>
            <span v-if="isFuenteDocumentBacked(fuente)" class="aip-fuente-doc-badge" title="Esta fuente puede leer el contenido de documentos adjuntos (OCR/Document Intelligence)">
              <i class="fas fa-file-alt"></i> Documental
            </span>
          </div>
          <p v-if="fuente.description" class="aip-fuente-card-desc">{{ fuente.description }}</p>

          <!--
            Flag/límites de lectura de documentos (23 sep 2026) -- solo para fuentes marcadas
            como documentales en el catálogo (CODE_AIPromptSource.DocumentBackedFlag, hoy solo
            PruebasAcademicas). Persisten en AIPromptConfigurationSource.IncludeDocumentContentFlag/
            MaxDocuments/MaxDocumentChars (ver AIPromptConfigurationEngine.ApplyDocumentSourceOptions);
            en null/sin tocar, el motor usa sus valores por defecto
            (PeiAIContextBuilderEngine._DefaultMaxDocumentsPerSource/_DefaultMaxCharsPerDocument).
          -->
          <div v-if="isFuenteDocumentBacked(fuente)" class="aip-fuente-doc-options">
            <custom-checkbox-single-option
              :value="documentOptionsFor(fuente.name).includeDocumentContent"
              option-text="Incluir contenido de los documentos adjuntos (lectura OCR/Document Intelligence)"
              :disabled="readOnly"
              @input="(checked) => setDocumentOptionField(fuente.name, 'includeDocumentContent', checked)"
            />
            <div v-if="documentOptionsFor(fuente.name).includeDocumentContent" class="row mt-2">
              <div class="col-12 col-md-6">
                <custom-input
                  :value="documentOptionsFor(fuente.name).maxDocuments"
                  @input="(val) => setDocumentOptionField(fuente.name, 'maxDocuments', val === '' ? null : Number(val))"
                  type="number"
                  :min="1"
                  label="Máx. documentos a leer"
                  placeholder="Por defecto: 3"
                  :disabled="readOnly"
                />
              </div>
              <div class="col-12 col-md-6">
                <custom-input
                  :value="documentOptionsFor(fuente.name).maxDocumentChars"
                  @input="(val) => setDocumentOptionField(fuente.name, 'maxDocumentChars', val === '' ? null : Number(val))"
                  type="number"
                  :min="1"
                  label="Máx. caracteres por documento"
                  placeholder="Por defecto: 12000"
                  :disabled="readOnly"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="aip-card mt-3">
      <!-- Sección colapsable hecha a mano: custom-collapse (la única librería de "collapse" del
           proyecto) resultó ser un wrapper trivial (un div con id, sin lógica propia), y su hijo
           CustomCollapseItem nunca se pudo confirmar leyendo su código fuente real -- así que en
           vez de depender de un contrato no verificado, esto es un simple toggle booleano
           (showAdvanced) + v-show. -->
      <div
        class="aip-section-header d-flex align-items-center justify-content-between aip-collapse-toggle"
        @click="showAdvanced = !showAdvanced"
      >
        <div class="d-flex align-items-center aip-icon-title-row">
          <span class="aip-section-icon"><i class="fas fa-sliders-h"></i></span>
          <SectionTitle has-line content="Parámetros avanzados del modelo" />
        </div>
        <i class="fas" :class="showAdvanced ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      </div>

      <div v-show="showAdvanced" class="mt-2">
        <p class="hint mb-2">
          Ajustan cómo redacta el modelo (extensión máxima de la respuesta y qué tan variada es
          la redacción entre corridas) -- no afectan qué datos ve ni qué fuentes se resuelven.
        </p>
        <div v-if="!readOnly" class="d-flex justify-content-end mb-2">
          <!--
            Valores "recomendados" tomados de los 4 registros reales sembrados en
            data/CODE_AIPromptConfiguration.sql (todos usan MaxTokens 900 / Temperature 0.30 /
            TopP 0.90) -- no son un rango inventado para este botón.
          -->
          <button type="button" class="aip-btn-reset" @click="resetToRecommendedValues">
            <i class="fas fa-undo mr-1"></i>Restablecer valores recomendados
          </button>
        </div>

        <div class="row">
          <!--
            CustomInput trae :min/:max con default 0/100 -- sin pisarlos explícitamente, MaxTokens
            (que necesita valores como 900-4000) quedaría con un tope nativo de 100. :step también
            se pasa siempre como Number (bound), no como string estático, porque la prop lo declara
            type: Number.
          -->
          <div class="col-12 col-md-4 mt-2">
            <custom-input
              v-model.number="value.maxTokens"
              type="number"
              :min="1"
              :max="32000"
              label="Max Tokens"
              placeholder="Ej. 900"
              :disabled="readOnly"
            />
            <p class="hint mb-0">Recomendado: 900</p>
          </div>
          <div class="col-12 col-md-4 mt-2">
            <custom-input
              v-model.number="value.temperature"
              type="number"
              :step="0.05"
              :min="0"
              :max="2"
              label="Temperature"
              placeholder="Ej. 0.30"
              :disabled="readOnly"
            />
            <p class="hint mb-0">Recomendado: 0.30</p>
          </div>
          <div class="col-12 col-md-4 mt-2">
            <custom-input
              v-model.number="value.topP"
              type="number"
              :step="0.05"
              :min="0"
              :max="1"
              label="Top P"
              placeholder="Ej. 0.90"
              :disabled="readOnly"
            />
            <p class="hint mb-0">Recomendado: 0.90</p>
          </div>
        </div>

      </div>
    </div>

    <div class="aip-card mt-3">
      <!-- Mismo patrón de colapsable hecho a mano que "Parámetros avanzados del modelo" arriba
           (custom-collapse resultó ser un wrapper sin contrato verificado -- ver ese comentario). -->
      <div
        class="aip-section-header d-flex align-items-center justify-content-between aip-collapse-toggle"
        @click="showOutputFormat = !showOutputFormat"
      >
        <div class="d-flex align-items-center aip-icon-title-row">
          <span class="aip-section-icon"><i class="fas fa-rocket"></i></span>
          <SectionTitle has-line content="Formato de salida (JSON Schema)" />
        </div>
        <i class="fas" :class="showOutputFormat ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      </div>

      <div v-show="showOutputFormat" class="mt-2">
        <p class="hint mb-2">
          Mapea cada clave que debe devolver el modelo hacia el campo real del PEI que la
          recibe -- lo que hoy vive en CODE_AIPromptConfiguration.ResponseJsonSchema (y, si
          alguna clave está restringida a un catálogo en vivo, en EnumSourcesJson), sin editar
          JSON a mano. Déjalo vacío para respuesta de texto libre (comportamiento por defecto).
        </p>

        <!--
          Nombre del schema: autogenerado desde Módulo + Sección/Área, ya NO editable a mano.
          Regla de slug verificada contra los 2 casos reales que existen hoy en
          data/CODE_AIPromptConfiguration.sql (pei_parte_iii_a / pei_parte_iii_b) -- ver
          autoResponseJsonSchemaName(). Si un módulo futuro no sigue este patrón, esta regla
          tendría que revisarse entonces (no se puede generalizar sin más casos reales).
        -->
        <div class="aip-schema-name-row mb-3">
          <span class="aip-schema-name-label">Nombre del schema</span>
          <span class="aip-badge aip-badge-locked">{{ value.responseJsonSchemaName || '—' }}</span>
          <span v-if="value.sectionArea" class="aip-badge aip-badge-muted">{{ value.sectionArea }}</span>
          <span class="aip-lock-hint"><i class="fas fa-lock mr-1"></i>autogenerado — no editable</span>
        </div>

        <p class="hint mb-2">
          Autogenerado a partir de Módulo + Sección/Área, no se edita a mano.
        </p>

        <div v-if="parsedSchema.valid && !rawJsonEditMode" class="aip-schema-table-wrap">
          <table class="aip-schema-table">
            <thead>
              <tr>
                <th>Clave JSON</th>
                <th>Tipo</th>
                <th>Descripción para el modelo</th>
                <th>Enum en vivo</th>
                <th>Requerido</th>
                <th v-if="!readOnly"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in parsedSchema.rows" :key="idx">
                <td>
                  <custom-input
                    label=""
                    :value="row.key"
                    placeholder="ej. descripcion"
                    :disabled="readOnly"
                    @input="(val) => updateSchemaKeyField(idx, 'key', val)"
                  />
                </td>
                <td><span class="aip-badge aip-badge-type">string</span></td>
                <td>
                  <custom-input
                    label=""
                    :value="row.description"
                    placeholder="Qué debe contener esta clave"
                    :disabled="readOnly"
                    @input="(val) => updateSchemaKeyField(idx, 'description', val)"
                  />
                </td>
                <td>
                  <custom-drop-down
                    label=""
                    :value="enumSourceOption(row.enumSource)"
                    :options="enumSourceOptions"
                    fieldkey="value"
                    fieldname="text"
                    :disabled="readOnly"
                    @input="(opt) => updateSchemaKeyField(idx, 'enumSource', opt ? opt.value : '')"
                  />
                </td>
                <td class="text-center">
                  <input
                    type="checkbox"
                    class="aip-schema-required-check"
                    :checked="row.required"
                    :disabled="readOnly"
                    @change="(e) => updateSchemaKeyField(idx, 'required', e.target.checked)"
                  />
                </td>
                <td v-if="!readOnly">
                  <button
                    type="button"
                    class="aip-schema-remove-btn"
                    title="Quitar esta clave"
                    @click="removeSchemaKey(idx)"
                  >
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="!parsedSchema.rows.length">
                <td :colspan="readOnly ? 5 : 6" class="aip-schema-empty">
                  Sin claves definidas -- esta instrucción responde con texto libre.
                </td>
              </tr>
            </tbody>
          </table>

          <p v-if="hasEmptySchemaKey" class="aip-schema-warning">
            <i class="fas fa-exclamation-triangle mr-1"></i>
            Hay una clave sin nombre -- complétala antes de publicar esta instrucción.
          </p>
          <p v-if="duplicateSchemaKeys.length" class="aip-schema-warning">
            <i class="fas fa-exclamation-triangle mr-1"></i>
            Clave repetida: {{ duplicateSchemaKeys.join(', ') }} -- cada clave del JSON debe ser
            única, la última que escribas es la que queda al guardar.
          </p>

          <button
            v-if="!readOnly"
            type="button"
            class="aip-btn-add-key mt-2"
            @click="addSchemaKey"
          >
            <i class="fas fa-plus mr-1"></i>Agregar clave del JSON
          </button>

          <p class="hint mt-3 mb-1">
            "Enum en vivo" es la columna real EnumSourcesJson -- aparte de ResponseJsonSchema. Si
            se marca, esa clave queda restringida a un catálogo resuelto en el momento de generar
            (hoy el único implementado es NivelFuncionamiento, ver PeiAIEnumSource.cs), en vez de
            texto libre.
          </p>

          <div v-if="value.responseJsonSchema" class="aip-json-preview-wrap mt-2">
            <div class="aip-json-preview-label">Vista previa -- ResponseJsonSchema</div>
            <pre
              v-if="highlightedResponseJsonSchema"
              class="aip-json-preview"
              v-html="highlightedResponseJsonSchema"
            ></pre>
          </div>
          <div v-if="value.enumSourcesJson" class="aip-json-preview-wrap mt-2">
            <div class="aip-json-preview-label">Vista previa -- EnumSourcesJson</div>
            <pre
              v-if="highlightedEnumSourcesJson"
              class="aip-json-preview"
              v-html="highlightedEnumSourcesJson"
            ></pre>
          </div>
        </div>

        <!--
          Modo crudo -- puerta de escape para un ResponseJsonSchema que no encaje en el patrón
          simple de este constructor (todas las claves reales hoy son "type: string" con
          description/required/additionalProperties:false -- ver los 4 registros sembrados), o
          para cuando el usuario elige editar el JSON a mano voluntariamente (rawJsonEditMode).
          Se fuerza automáticamente si el JSON guardado no calza con ese patrón (parsedSchema.valid
          = false), para no ocultar ni corromper datos reales que el constructor no sabe modelar.
        -->
        <div v-else>
          <p v-if="!parsedSchema.valid" class="aip-schema-warning mb-2">
            <i class="fas fa-exclamation-triangle mr-1"></i>
            El ResponseJsonSchema guardado no tiene la forma simple que este constructor sabe
            editar (type object, properties de tipo string, required, additionalProperties
            false). Edítalo como JSON crudo abajo.
          </p>
          <custom-text-area
            v-model="value.responseJsonSchema"
            label="Response JSON Schema"
            :rows="8"
            :maxlength="20000"
            placeholder='{"type":"object","properties":{...}}'
            :error="jsonSchemaError"
            :disabled="readOnly"
          />
          <custom-text-area
            v-model="value.enumSourcesJson"
            label="Enum Sources JSON"
            class="mt-2"
            :rows="4"
            :maxlength="4000"
            placeholder='{"aprovechamiento":"NivelFuncionamiento"}'
            :error="enumSourcesError"
            :disabled="readOnly"
          />
        </div>

        <!--
          Este toggle solo tiene sentido cuando el schema SÍ calza con el patrón simple -- si
          parsedSchema.valid es false, el modo crudo ya está forzado arriba (no hay "editor de
          claves" al cual volver hasta que el JSON tenga esa forma otra vez).
        -->
        <button
          v-if="!readOnly && parsedSchema.valid"
          type="button"
          class="aip-btn-reset aip-btn-raw-toggle mt-2"
          @click="rawJsonEditMode = !rawJsonEditMode"
        >
          <i class="fas fa-code mr-1"></i>
          {{ rawJsonEditMode ? 'Volver al editor de claves' : 'Editar JSON manualmente' }}
        </button>
      </div>
    </div>

    <!--
      Buenas prácticas: contenido que el panel del Asistente muestra al especialista debajo del
      cuadro de refinar (AiAssistantModal.vue -> bloque .ai-modal-tips). Se administra acá, por
      Módulo/Sección-Área, en vez de quemarlo en el frontend. El título y el enlace son columnas
      de CODE_AIPromptConfiguration; cada punto es una fila de AIPromptConfigurationBestPractice.
      OJO: esto NO entra al prompt ni al contexto que se le manda al modelo -- es solo un
      recordatorio de redacción para quien revisa la sugerencia.
    -->
    <div class="aip-card mt-3">
      <div class="aip-section-header aip-icon-title-row d-flex align-items-center mb-2">
        <span class="aip-section-icon"><i class="fas fa-lightbulb"></i></span>
        <SectionTitle has-line content="Buenas prácticas (panel del Asistente)" />
      </div>

      <p class="hint mb-2">
        Recordatorio de redacción que se muestra en el panel del Asistente para esta
        Sección/Área. No afecta lo que redacta el modelo. Si no hay título o no hay ningún
        punto, la caja no se muestra.
      </p>

      <custom-input
        v-model="value.bestPracticesTitle"
        label="Título de la caja"
        placeholder="Ej. Buenas prácticas para redacción del PEI"
        maxlength="200"
        :disabled="readOnly"
      />

      <label class="aip-fuentes-label mt-2">Puntos de la lista</label>

      <div
        v-for="(punto, index) in bestPractices"
        :key="index"
        class="d-flex align-items-center aip-bp-row"
      >
        <div class="flex-grow-1">
          <custom-input
            :value="punto"
            placeholder="Ej. Use lenguaje claro, observable y medible."
            maxlength="500"
            :disabled="readOnly"
            @input="(nuevoTexto) => updateBuenaPractica(index, nuevoTexto)"
          />
        </div>
        <button
          v-if="!readOnly"
          type="button"
          class="aip-btn-reset aip-bp-remove"
          title="Quitar punto"
          @click="removeBuenaPractica(index)"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <p v-if="!bestPractices.length" class="hint mb-1">
        Todavía no hay puntos configurados para esta instrucción.
      </p>

      <button
        v-if="!readOnly"
        type="button"
        class="aip-btn-reset aip-btn-raw-toggle"
        @click="addBuenaPractica"
      >
        <i class="fas fa-plus mr-1"></i>
        Agregar punto
      </button>

      <div class="row mt-2">
        <div class="col-12 col-md-6">
          <custom-input
            v-model="value.bestPracticesLinkText"
            label="Texto del enlace (opcional)"
            placeholder="Ej. Ver más recomendaciones"
            maxlength="100"
            :disabled="readOnly"
          />
        </div>
        <div class="col-12 col-md-6">
          <custom-input
            v-model="value.bestPracticesLinkUrl"
            label="URL del enlace (opcional)"
            placeholder="https://..."
            maxlength="500"
            :disabled="readOnly"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationProvider } from 'vee-validate';
import SectionTitle from 'SectionTitle';
import CustomInput from 'CustomInput';
import CustomDropDown from 'custom-drop-down';
// Nombre real confirmado en package.json de EduEsp2FrontEnd: "CustomTextArea" (PascalCase,
// no "custom-text-area" -- a diferencia de custom-drop-down/custom-checkbox-single-option,
// este paquete sigue la misma convención PascalCase que CustomInput/ActionButton/PageHeader).
import CustomTextArea from 'CustomTextArea';
import CustomCheckboxSingleOption from 'custom-checkbox-single-option';
import {
  CURRICULOS_REFERENCIA,
  ENUM_SOURCES_CATALOG,
} from '@/utils/constants/aiPromptConfigOptions';
import {
  getFuentesCatalogo,
  getModulosCatalogo,
  getSeccionesCatalogo,
} from '@/modules/operation-ai-prompt-management/api';

export default {
  name: 'AiPromptConfigCreateEditForm',
  components: {
    ValidationProvider,
    SectionTitle,
    CustomInput,
    CustomDropDown,
    CustomTextArea,
    CustomCheckboxSingleOption,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    // Modo solo-lectura: deshabilita todos los campos (Módulo/Sección ya se deshabilitan
    // aparte vía isEditMode) y oculta el botón "Restablecer valores recomendados". Lo activa
    // CreateEdit.vue cuando se llega desde el botón "Ver" del historial -- antes "Ver" abría
    // este mismo formulario totalmente editable, lo cual confundía (ver AiPromptConfigHistoryRow).
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      modulosCatalogo: [],
      seccionesCatalogo: [],
      curriculos: CURRICULOS_REFERENCIA,
      enumSourceOptions: ENUM_SOURCES_CATALOG,
      fuentesCatalogo: [],
      loadingFuentes: false,
      loadingCatalogos: false,
      // Colapsada por defecto -- "Parámetros avanzados del modelo" agrupa solo los parámetros
      // de sampling (MaxTokens/Temperature/TopP), opcionales. En modo solo-lectura arranca
      // abierta, para que quien solo está viendo no tenga que hacer clic para ver todo.
      showAdvanced: this.readOnly,
      // "Formato de salida (JSON Schema)" -- sección propia desde el 25 ago 2026 (antes vivía
      // adentro de Parámetros avanzados, mezclada con MaxTokens/Temperature/TopP). Mismo criterio
      // de colapsado por defecto / abierta en solo-lectura.
      showOutputFormat: this.readOnly,
      // Puerta de escape manual al modo de JSON crudo -- ver comentario junto a la tabla en el
      // <template>. Independiente de si parsedSchema.valid es true o false.
      rawJsonEditMode: false,
      // Panel de "Fuentes de contexto" colapsado por defecto (igual criterio que showAdvanced):
      // en modo solo-lectura arranca expandido para que quien solo está viendo no tenga que
      // hacer clic para ver qué fuentes trae marcadas.
      fuentesExpanded: this.readOnly,
      // "{{CONTEXTO}}" NO puede escribirse literal en el <template> -- el compilador de Vue
      // 2 interpreta cualquier "{{ }}" como interpolación (incluso dentro de un atributo
      // estático, donde directamente lo prohíbe) y revienta el build. Viviendo en data() es
      // un string JS normal; el template solo interpola la VARIABLE (contextoToken), no las
      // llaves que contiene.
      contextoToken: '{{CONTEXTO}}',
    };
  },
  computed: {
    // Blindaje: una instrucción vieja (o el modelo vacío antes de cargar) puede no traer el
    // arreglo -- el v-for necesita siempre una lista.
    bestPractices() {
      return Array.isArray(this.value.bestPractices) ? this.value.bestPractices : [];
    },
    promptTextPlaceholder() {
      return `Redacta la Parte... usando el contexto a continuación. CONTEXTO: ${this.contextoToken}`;
    },
    // Módulo y Sección/Área se persisten como NAME (string), no como el Id numérico del
    // catálogo -- así se mantiene compatible con CreateDraftRequest/GetPublished/GetHistory,
    // que siguen recibiendo "modulo"/"sectionArea" como string (ver
    // AIPromptConfigurationController.cs). El catálogo real solo alimenta las opciones del
    // dropdown y el filtrado de Secciones por Módulo.
    modulos() {
      return this.modulosCatalogo.map((m) => ({ value: m.name, text: m.name }));
    },
    secciones() {
      const moduloSeleccionado = this.modulosCatalogo.find(
        (m) => m.name === this.value.module
      );
      // Sin Módulo seleccionado todavía, se muestra el catálogo completo de Secciones en vez
      // de una lista vacía -- hoy solo existe un Módulo (PEI) así que en la práctica es lo
      // mismo, pero evita bloquear el dropdown si algún día hay más de uno.
      const filtradas = moduloSeleccionado
        ? this.seccionesCatalogo.filter(
            (s) => s.aiPromptModuleId === moduloSeleccionado.aiPromptModuleId
          )
        : this.seccionesCatalogo;
      return filtradas.map((s) => ({ value: s.name, text: s.name }));
    },
    module: {
      get() {
        return this.modulos.find((m) => m.value === this.value.module) || null;
      },
      set(option) {
        this.value.module = option ? option.value : null;
      },
    },
    sectionArea: {
      get() {
        return (
          this.secciones.find((s) => s.value === this.value.sectionArea) || null
        );
      },
      set(option) {
        this.value.sectionArea = option ? option.value : null;
      },
    },
    jsonSchemaError() {
      return this.jsonError(this.value.responseJsonSchema);
    },
    enumSourcesError() {
      return this.jsonError(this.value.enumSourcesJson);
    },
    highlightedResponseJsonSchema() {
      return this.highlightJson(this.value.responseJsonSchema);
    },
    highlightedEnumSourcesJson() {
      return this.highlightJson(this.value.enumSourcesJson);
    },
    // Fuentes del catálogo real que están marcadas en este momento, en el mismo orden real
    // (Sort) en el que ya vienen de GetFuentesCatalogoAsync -- se usa para las tarjetas de
    // detalle debajo del selector.
    fuentesSeleccionadas() {
      return this.fuentesCatalogo.filter((fuente) => this.isFuenteSeleccionada(fuente.name));
    },
    fuentesSeleccionadasLabel() {
      const total = (this.value.sources || []).length;
      if (total === 0) {
        return 'Ninguna seleccionada';
      }
      return `${total} seleccionada${total === 1 ? '' : 's'}`;
    },
    // Nombre del schema autogenerado desde Módulo + Sección/Área -- regla de slug verificada
    // contra los 2 casos reales de hoy (PEI + III-A -> "pei_parte_iii_a", PEI + III-B ->
    // "pei_parte_iii_b", ver data/CODE_AIPromptConfiguration.sql). Se sincroniza a
    // value.responseJsonSchemaName vía el watcher de abajo -- el campo ya no es editable a mano.
    autoResponseJsonSchemaName() {
      if (!this.value.module || !this.value.sectionArea) {
        return '';
      }
      return `${this.slugify(this.value.module)}_parte_${this.slugify(this.value.sectionArea)}`;
    },
    // Interpreta value.responseJsonSchema como el constructor de claves sabe editarlo. Solo se
    // considera "simple" (valid: true) si calza EXACTO con la forma que tienen los 4 registros
    // reales sembrados hoy: type object, cada property type string, required + additionalProperties
    // false. Si no calza (JSON inválido, u otra forma -- ej. type distinto de string, propiedades
    // anidadas), valid queda false y el <template> cae automáticamente al modo de JSON crudo en
    // vez de intentar editar/corromper algo que este constructor no sabe modelar.
    parsedSchema() {
      const raw = this.value.responseJsonSchema;
      if (!raw || !raw.trim()) {
        return { valid: true, rows: [] };
      }
      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        return { valid: false, rows: [] };
      }
      const isSimpleShape =
        parsed &&
        typeof parsed === 'object' &&
        parsed.type === 'object' &&
        parsed.properties &&
        typeof parsed.properties === 'object' &&
        !Array.isArray(parsed.properties) &&
        Object.values(parsed.properties).every(
          (prop) => prop && typeof prop === 'object' && prop.type === 'string'
        );
      if (!isSimpleShape) {
        return { valid: false, rows: [] };
      }
      const required = Array.isArray(parsed.required) ? parsed.required : [];
      let enumMap = {};
      if (this.value.enumSourcesJson && this.value.enumSourcesJson.trim()) {
        try {
          enumMap = JSON.parse(this.value.enumSourcesJson) || {};
        } catch (e) {
          enumMap = {};
        }
      }
      const rows = Object.keys(parsed.properties).map((key) => ({
        key,
        description: parsed.properties[key].description || '',
        required: required.includes(key),
        enumSource: enumMap[key] || '',
      }));
      return { valid: true, rows };
    },
    duplicateSchemaKeys() {
      const seen = {};
      const dupes = [];
      this.parsedSchema.rows.forEach((row) => {
        const key = (row.key || '').trim();
        if (!key) {
          return;
        }
        if (seen[key]) {
          if (!dupes.includes(key)) {
            dupes.push(key);
          }
        } else {
          seen[key] = true;
        }
      });
      return dupes;
    },
    hasEmptySchemaKey() {
      return this.parsedSchema.rows.some((row) => !row.key || !row.key.trim());
    },
  },
  watch: {
    autoResponseJsonSchemaName: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.value.responseJsonSchemaName = newVal;
        }
      },
    },
  },
  methods: {
    // Los puntos viven en el modelo como arreglo de strings (value.bestPractices) y se
    // guardan en AIPromptConfigurationBestPractice, una fila por punto, en este mismo orden.
    addBuenaPractica() {
      if(!Array.isArray(this.value.bestPractices)) {
        this.$set(this.value, 'bestPractices', []);
      }

      this.value.bestPractices.push('');
    },
    updateBuenaPractica(index, nuevoTexto) {
      this.value.bestPractices.splice(index, 1, nuevoTexto);
    },
    removeBuenaPractica(index) {
      this.value.bestPractices.splice(index, 1);
    },
    slugify(text) {
      return (text || '')
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    },
    enumSourceOption(value) {
      return (
        this.enumSourceOptions.find((opt) => opt.value === (value || '')) ||
        this.enumSourceOptions[0]
      );
    },
    updateSchemaKeyField(idx, field, val) {
      const rows = this.parsedSchema.rows.map((row) => ({ ...row }));
      if (!rows[idx]) {
        return;
      }
      rows[idx][field] = val;
      this.rebuildSchemaFromRows(rows);
    },
    addSchemaKey() {
      const rows = this.parsedSchema.rows.map((row) => ({ ...row }));
      // required:true por defecto -- en los 4 registros reales sembrados hoy, TODAS las claves
      // definidas están en el arreglo "required" (no hay ninguna clave opcional en la práctica).
      rows.push({ key: '', description: '', required: true, enumSource: '' });
      this.rebuildSchemaFromRows(rows);
    },
    removeSchemaKey(idx) {
      const rows = this.parsedSchema.rows.filter((row, i) => i !== idx);
      this.rebuildSchemaFromRows(rows);
    },
    // Reconstruye value.responseJsonSchema/value.enumSourcesJson desde las filas de la tabla --
    // única dirección de escritura del constructor (ver parsedSchema para la lectura). Si dos
    // filas comparten la misma clave, la última en el arreglo prevalece (Object assign en orden),
    // igual que advierte el mensaje de duplicateSchemaKeys en el template.
    rebuildSchemaFromRows(rows) {
      if (!rows.length) {
        this.value.responseJsonSchema = '';
        this.value.enumSourcesJson = '';
        return;
      }
      const properties = {};
      const required = [];
      const enumSources = {};
      rows.forEach((row) => {
        const key = row.key || '';
        properties[key] = {
          type: 'string',
          description: row.description || '',
        };
        if (row.required && !required.includes(key)) {
          required.push(key);
        }
        if (row.enumSource) {
          enumSources[key] = row.enumSource;
        }
      });
      const schema = {
        type: 'object',
        properties,
        required,
        additionalProperties: false,
      };
      this.value.responseJsonSchema = JSON.stringify(schema, null, 4);
      this.value.enumSourcesJson = Object.keys(enumSources).length
        ? JSON.stringify(enumSources)
        : '';
    },
    isFuenteSeleccionada(nombre) {
      return (this.value.sources || []).includes(nombre);
    },
    onFuenteToggle(nombre, checked) {
      const actuales = this.value.sources || [];
      this.value.sources = checked
        ? [...actuales, nombre]
        : actuales.filter((f) => f !== nombre);
    },
    isFuenteDocumentBacked(fuente) {
      // DocumentBackedFlag es VARCHAR(1) en BD -- llega como string ('0'/'1'), igual que
      // ImplementedFlag, no como boolean.
      return Boolean(fuente && fuente.documentBackedFlag === '1');
    },
    documentOptionsFor(nombre) {
      const todas = this.value.documentSourceOptions || {};
      const opciones = todas[nombre] || {};
      return {
        includeDocumentContent: opciones.includeDocumentContent !== false,
        maxDocuments: opciones.maxDocuments != null ? opciones.maxDocuments : null,
        maxDocumentChars: opciones.maxDocumentChars != null ? opciones.maxDocumentChars : null,
      };
    },
    setDocumentOptionField(nombre, campo, val) {
      if (!this.value.documentSourceOptions) {
        this.$set(this.value, 'documentSourceOptions', {});
      }
      const actuales = this.documentOptionsFor(nombre);
      this.$set(this.value.documentSourceOptions, nombre, { ...actuales, [campo]: val });
    },
    // Solo inserta espacios entre palabras de CODE_AIPromptSource.Name (ej. "DatosEstudiante"
    // -> "Datos Estudiante") para que se lea mejor en la tarjeta -- es el mismo valor real
    // reformateado, no una etiqueta ni una traducción inventada.
    formatFuenteName(name) {
      if (!name) {
        return '';
      }
      return name
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
    },
    jsonError(text) {
      if (!text) {
        return '';
      }
      try {
        JSON.parse(text);
        return '';
      } catch (e) {
        return 'Este campo debe ser un JSON válido.';
      }
    },
    escapeHtml(str) {
      return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    },
    /**
     * Arma un preview de JSON "resaltado" a mano (sin librería -- no hay ninguna en este
     * proyecto, ver comentario en el template). Devuelve:
     * - '' si el texto está vacío,
     * - null si el texto no es JSON válido (el template entonces cae a un <pre> plano),
     * - el HTML resaltado (con <span> por tipo de token) si es válido.
     */
    highlightJson(rawText) {
      if (!rawText || !rawText.trim()) {
        return '';
      }

      let parsed;
      try {
        parsed = JSON.parse(rawText);
      } catch (e) {
        return null;
      }

      const pretty = JSON.stringify(parsed, null, 2);
      const escaped = this.escapeHtml(pretty);

      const tokenRegex = /("(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^"\\])*"(\s*:)?)|\b(true|false)\b|\bnull\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g;

      return escaped.replace(tokenRegex, (match) => {
        if (match.charAt(0) === '"') {
          const isKey = /:\s*$/.test(match);
          return `<span class="${isKey ? 'aip-tok-key' : 'aip-tok-string'}">${match}</span>`;
        }
        if (match === 'true' || match === 'false') {
          return `<span class="aip-tok-bool">${match}</span>`;
        }
        if (match === 'null') {
          return `<span class="aip-tok-null">${match}</span>`;
        }
        return `<span class="aip-tok-number">${match}</span>`;
      });
    },
    resetToRecommendedValues() {
      this.value.maxTokens = 900;
      this.value.temperature = 0.3;
      this.value.topP = 0.9;
    },
    async loadFuentesCatalogo() {
      this.loadingFuentes = true;
      try {
        this.fuentesCatalogo = await getFuentesCatalogo();
      } finally {
        this.loadingFuentes = false;
      }
    },
    async loadCatalogos() {
      this.loadingCatalogos = true;
      try {
        const [modulos, secciones] = await Promise.all([
          getModulosCatalogo(),
          getSeccionesCatalogo(),
        ]);
        this.modulosCatalogo = modulos;
        this.seccionesCatalogo = secciones;
      } finally {
        this.loadingCatalogos = false;
      }
    },
  },
  mounted() {
    this.loadFuentesCatalogo();
    this.loadCatalogos();
  },
};
</script>

<style scoped>
/*
  Paleta y layout de este archivo inspirados en el mockup entregado (24 ago 2026) -- SOLO
  aplican a esta pantalla: al ser <style scoped>, Vue les agrega un atributo data-v-xxxx único
  y los limita a los elementos que renderiza este componente, sin tocar el resto de MiPE.
*/
.aip-form {
  --aip-coral: #ff6b57;
  --aip-coral-dark: #e2543f;
  --aip-charcoal-green: #17332c;
  --aip-card-bg: #f7f8f9;
  --aip-card-border: #ececec;
}

.aip-card {
  background: var(--aip-card-bg);
  border: 1px solid var(--aip-card-border);
  border-radius: 10px;
  padding: 16px 18px;
}

.aip-section-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 5px;
  background: rgba(255, 107, 87, 0.15);
  color: var(--aip-coral-dark);
  font-size: 11px;
  margin-right: 8px;
  flex: 0 0 auto;
}

/*
  SectionTitle (paquete compartido "SectionTitle", node_modules/SectionTitle -- no es un
  componente propio de este archivo, así que no se edita directo) renderiza su título dentro de
  un <div> que, con has-line, incluye además la línea divisoria debajo del texto -- eso hace que
  ese <div> completo sea más alto que el ícono de 22px de al lado. Con align-items: center (el
  default de Bootstrap en .d-flex.align-items-center), el ícono se centra contra ESE bloque
  completo (título + línea), no contra el texto en sí, y queda visualmente más abajo que el
  título. La fila icono+título se alinea arriba en su lugar (align-items: flex-start), y el
  ícono se empuja los mismos 5px que SectionTitle ya usa internamente en su propio título
  (.tag-badge-title-info { margin-top: 5px }) para que ambos arranquen a la misma altura.
*/
.aip-icon-title-row {
  align-items: flex-start !important;
}

.aip-icon-title-row .aip-section-icon {
  margin-top: 5px;
}

.aip-collapse-toggle {
  cursor: pointer;
  user-select: none;
}

.aip-bp-row {
  gap: 8px;
}

.aip-bp-remove {
  color: #c9545e;
  padding: 0 6px;
}

.aip-btn-reset {
  background: transparent;
  border: 1px solid var(--aip-charcoal-green);
  color: var(--aip-charcoal-green);
  border-radius: 6px;
  font-size: 12.5px;
  padding: 4px 10px;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.aip-btn-reset:hover {
  background: var(--aip-charcoal-green);
  color: #fff;
}

/* CustomTextArea envuelve un <textarea> nativo -- se estira su alto mínimo para que el
   contenido (a veces varios párrafos largos) se vea completo, sin recortarse, como se pidió. */
.aip-textarea-full ::v-deep textarea {
  min-height: 260px;
}

.aip-json-preview-wrap {
  background: #1e1e1e;
  border-radius: 6px;
  padding: 8px 10px;
}

.aip-json-preview-label {
  color: #9a9a9a;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

.aip-json-preview {
  margin: 0;
  max-height: 260px;
  overflow: auto;
  font-family: 'SFMono-Regular', Consolas, Menlo, monospace;
  font-size: 12.5px;
  line-height: 1.5;
  color: #d4d4d4;
  white-space: pre-wrap;
  word-break: break-word;
}

.aip-json-preview-invalid {
  color: #f0a3a3;
}

.aip-tok-key {
  color: #9cdcfe;
}
.aip-tok-string {
  color: #ce9178;
}
.aip-tok-number {
  color: #b5cea8;
}
.aip-tok-bool {
  color: #569cd6;
}
.aip-tok-null {
  color: #808080;
}

.hint {
  font-size: 12.5px;
  color: #6b6b6b;
  margin: 4px 0 0;
}

/*
  26 ago 2026: mismo patrón role-label/role-hint del HTML propuesto (bloque de "Contenido de la
  instrucción") -- una etiqueta corta y en negrita identificando el rol real que cumple ese
  mensaje al llamar al modelo (system/user), seguida de una explicación breve en gris de qué se
  supone que va ahí. Reusa la paleta ya establecida en este archivo (verde-charcoal + gris de
  .hint), no introduce colores nuevos.
*/
.aip-role-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: var(--aip-charcoal-green);
  margin: 0 0 2px;
}

.aip-role-tag {
  font-weight: 400;
  color: #6b6b6b;
  font-size: 11px;
}

.aip-role-hint {
  font-size: 12px;
  color: #6b6b6b;
  line-height: 1.5;
  margin: 0 0 8px;
}

/* Selector colapsable de "Fuentes de contexto" -- paleta tomada del mockup entregado (25 ago
   2026): borde/ícono verde-menta cuando está disponible, gris cuando está deshabilitado. */
.aip-fuentes-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--aip-charcoal-green);
  margin-bottom: 6px;
}

.aip-multiselect-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #bfe2d3;
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 13.5px;
  color: var(--aip-charcoal-green);
  transition: border-color 0.15s ease;
}

.aip-multiselect-trigger:hover:not(:disabled) {
  border-color: #5c8f76;
}

.aip-multiselect-open .aip-multiselect-trigger {
  border-color: #5c8f76;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.aip-multiselect-trigger i {
  color: #5c8f76;
  font-size: 12px;
}

.aip-multiselect-disabled .aip-multiselect-trigger {
  background: #f5f6f7;
  border-color: #dcdcdc;
  color: #9a9a9a;
  cursor: not-allowed;
}

.aip-multiselect-disabled .aip-multiselect-trigger i {
  color: #b3b3b3;
}

.aip-multiselect-panel {
  border: 1px solid #5c8f76;
  border-top: none;
  border-radius: 0 0 8px 8px;
  padding: 10px 12px;
  background: #fff;
  max-height: 320px;
  overflow-y: auto;
}

.aip-fuente-checkbox {
  margin-bottom: 4px;
}

/*
  custom-checkbox-single-option (paquete real, ver node_modules/custom-checkbox-single-option/
  src/CustomCheckboxSingleOption.vue) viene pensado para casillas grandes tipo "tarjeta"
  (min-height 66px, texto 16px, y al marcarse pinta TODA la fila -- ícono y texto -- de verde
  sólido con texto blanco). Igual que ya se hace más abajo con los botones ActionButton
  (.aip-btn-draft/.aip-btn-publish), se sobreescribe su estilo con ::v-deep para que quede
  compacto como el mockup (fila delgada, solo el cuadradito de verificación en verde, texto
  siempre oscuro sobre fondo claro) sin dejar de ser el componente real de MiPE.
*/
.aip-multiselect-panel ::v-deep .checkbox-container {
  min-height: 30px;
  width: 30px;
  border-radius: 5px 0 0 5px;
}
.aip-multiselect-panel ::v-deep .checkbox-container .input {
  height: 16px;
  width: 16px;
  margin: 7px;
  border-radius: 3px;
  /* El componente real solo define tamaño/color para el ::after (el ícono de check vive ahí,
     como pseudo-elemento) -- sin centrarlo explícitamente, a este tamaño reducido el check
     quedaba pegado a una esquina en vez de centrado en el cuadradito. */
  display: flex;
  align-items: center;
  justify-content: center;
}
.aip-multiselect-panel ::v-deep .checkbox-container .input::after {
  font-size: 11px;
  line-height: 1;
}
.aip-multiselect-panel ::v-deep .checkbox-word-container {
  min-height: 30px;
  border-radius: 0 5px 5px 0;
}
.aip-multiselect-panel ::v-deep .checkbox-word-container p {
  padding: 5px 10px;
  font-size: 12.5px;
  line-height: 1.3;
}
.aip-multiselect-panel ::v-deep .checkbox-word-container.green-box {
  background-color: #eef7f2;
  color: #17332c;
  border-color: #bfe2d3;
}

.aip-fuente-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.aip-fuente-card {
  background: #f0f4f1;
  border-radius: 8px;
  padding: 10px 14px;
}

.aip-fuente-card-title {
  font-size: 13px;
  font-weight: 700;
  color: #17332c;
  display: flex;
  align-items: center;
  gap: 6px;
}

.aip-fuente-card-icon {
  font-size: 12px;
  color: #5c8f76;
  cursor: help;
}

.aip-fuente-card-desc {
  font-size: 12.5px;
  color: #4d7a64;
  margin: 2px 0 0;
}

.aip-fuente-doc-badge {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #17332c;
  background: #e3f3ea;
  border-radius: 999px;
  padding: 1px 8px;
  margin-left: 4px;
}

.aip-fuente-doc-options {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #bfe2d3;
}

/* "Formato de salida (JSON Schema)" -- misma paleta ya establecida en este archivo (coral +
   verde-charcoal + acentos verde-menta de "Fuentes de contexto"), no la paleta del modal
   "Asistente Virtual" de Tab3.vue (esa es otra pantalla, con su propio verde #2DB782). */
.aip-schema-name-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.aip-schema-name-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--aip-charcoal-green);
}

.aip-badge {
  display: inline-flex;
  align-items: center;
  font-family: 'SFMono-Regular', Consolas, Menlo, monospace;
  font-size: 12.5px;
  padding: 3px 10px;
  border-radius: 20px;
  line-height: 1.4;
}

.aip-badge-locked {
  background: #eef7f2;
  border: 1px solid #bfe2d3;
  color: var(--aip-charcoal-green);
}

.aip-badge-muted {
  background: #f5f6f7;
  border: 1px solid #dcdcdc;
  color: #6b6b6b;
  font-family: inherit;
}

.aip-badge-type {
  background: rgba(255, 107, 87, 0.12);
  color: var(--aip-coral-dark);
  border: none;
}

.aip-lock-hint {
  font-size: 11.5px;
  color: #9a9a9a;
  margin-left: auto;
}

.aip-schema-table-wrap {
  overflow-x: auto;
}

.aip-schema-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 6px;
}

.aip-schema-table th {
  text-align: left;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b6b6b;
  font-weight: 600;
  padding: 0 8px 4px;
  white-space: nowrap;
}

.aip-schema-table td {
  background: #fff;
  border-top: 1px solid #ececec;
  border-bottom: 1px solid #ececec;
  padding: 6px 8px;
  vertical-align: middle;
}

.aip-schema-table td:first-child {
  border-left: 1px solid #ececec;
  border-radius: 6px 0 0 6px;
}

.aip-schema-table td:last-child {
  border-right: 1px solid #ececec;
  border-radius: 0 6px 6px 0;
}

.aip-schema-required-check {
  width: 16px;
  height: 16px;
  accent-color: #5c8f76;
  cursor: pointer;
}

.aip-schema-remove-btn {
  background: transparent;
  border: none;
  color: #b3261e;
  font-size: 13px;
  padding: 4px 6px;
  border-radius: 5px;
  transition: background-color 0.15s ease;
}

.aip-schema-remove-btn:hover {
  background: rgba(179, 38, 30, 0.08);
}

.aip-schema-empty {
  text-align: center;
  color: #9a9a9a;
  font-size: 12.5px;
  padding: 14px 8px !important;
  border-radius: 6px !important;
}

.aip-schema-warning {
  color: #b3261e;
  font-size: 12.5px;
  margin: 6px 0 0;
}

.aip-btn-add-key {
  background: transparent;
  border: 1px dashed #5c8f76;
  color: var(--aip-charcoal-green);
  border-radius: 6px;
  font-size: 12.5px;
  padding: 6px 12px;
  transition: background-color 0.15s ease;
}

.aip-btn-add-key:hover {
  background: #eef7f2;
}

.aip-btn-raw-toggle {
  display: block;
}
</style>
